/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
const JSG = require('../../JSG');
const ItemAttributes = require('../attr/ItemAttributes');
const Node = require('./Node');
const Point = require('../../geometry/Point');
const HeaderSection = require('./HeaderSection');


/**
 * @class HeaderNode
 *
 * @extends Node
 * @constructor
 */
module.exports = class HeaderNode extends Node {
	constructor() {
		super();

		this.getFormat().setLineColor(JSG.theme.frame);
		this.getFormat().setFillColor(JSG.theme.header);
		this.getTextFormat().setFontColor(JSG.theme.text);
		this.getItemAttributes().setSelectionMode(ItemAttributes.SelectionMode.NONE);
		this.getItemAttributes().setPortMode(ItemAttributes.PortMode.NONE);
		this.getItemAttributes().setContainer(false);
	}

	newInstance() {
		return new HeaderNode();
	}

	_copy(copiednodes, deep, ids) {
		const copy = super._copy(copiednodes, deep, ids);
		/*
		copy._sectionData = [];

		this._sectionData.forEach((section, index) => {
			if (section !== undefined) {
				copy._sectionData[index] = section.copy();
			}
		});
		*/
		return copy;
	}

	getSheet() {
		return this.getParent() ? this.getParent().getParent() : undefined;
	}

	clear() {
		this._data.clear();
	}

	getMinSize() {
		return 0;
	}

	getSections() {
		return this.sectionData.size;
	}

	getSectionData() {
		// must be overriden
	}

	get outlineDirection() {
		return this.sectionData.outlineDirection;
	}

	set outlineDirection(direction) {
		this.sectionData.outlineDirection = direction;
	}

	get sectionData() {
		return this._data;
	}

	set sectionData(data) {
		this._data = data;
		this.updateParents();
	}

	get viewRange() {
		return this._viewRange;
	}

	set viewRange(range) {
		this._viewRange = range;
	}

	get initialSection() {
		return this.sectionData.initialSection;
	}

	getSectionsSize() {
		return this.sectionData.getSectionsSize(this.viewRange);
	}

	getDefaultSectionSize() {
		return this.sectionData.defaultSectionSize;
	}

	setDefaultSectionSize(size) {
		this.sectionData.defaultSectionSize = size;
	}

	getSectionDefaults(index) {
		return this.sectionData.getSectionDefaults(index);
	}

	getSectionSize(index) {
		return this.sectionData.getSectionSize(index, this.viewRange);
	}

	setSectionClosed(index, closed) {
		this.sectionData.setSectionClosed(index, closed);
	}

	getSectionClosed(index) {
		return this.sectionData.getSectionClosed(index);
	}

	setSectionSize(index, size) {
		this.sectionData.setSectionSize(index, size);
	}

	getSectionLevel(index) {
		return this.sectionData.getSectionLevel(index);
	}

	setSectionLevel(index, level) {
		this.sectionData.setSectionLevel(index, level);
	}

	getSectionParent(index) {
		return this.sectionData.getSectionParent(index);
	}

	setSectionParent(index, parent) {
		this.sectionData.setSectionParent(index, parent);
	}

	setSectionVisible(index, visible) {
		this.sectionData.setSectionVisible(index, visible);
	}

	getSectionTitle(index) {
		return this.sectionData.getSectionTitle(index);
	}

	getSectionFormat(index) {
		return this.sectionData.getSectionFormat(index);
	}

	getSectionTextFormat(index) {
		return this.sectionData.getSectionTextFormat(index);
	}

	getSectionAttributes(index) {
		return this.sectionData.getSectionAttributes(index);
	}

	getSectionAt(index) {
		return this.sectionData.getSectionAt(index);
	}

	setSectionAt(index, data) {
		this.sectionData.setSectionAt(index, data);
	}

	getOrCreateSectionAt(index) {
		return this.sectionData.getOrCreateSectionAt(index);
	}

	getSection(pos) {
		return this.sectionData.getSection(pos, this.viewRange);
	}

	getSectionPos(index) {
		return this.sectionData.getSectionPos(index, this.viewRange);
	}

	getSectionSplit(pos) {
		return this.sectionData.getSectionSplit(pos, this.viewRange);
	}

	iterateSections(callback) {
		this.sectionData.iterateSections(callback);
	}

	getMaxLevel() {
		let level = 0;

		this.sectionData.sections.forEach((section) => {
			level = Math.max(section.level, level);
		});

		return level;
	}

	saveCondensed(writer, name) {
		this.updateParents();
		writer.writeStartElement(name);

		writer.writeAttributeString('outline', this.getHeaderAttributes().getOutlineDirection().getValue());

		writer.writeStartArray('section');

		this.sectionData.sections.forEach((data, index) => {
			data.save(writer, index);
		});

		writer.writeEndArray('section');
		writer.writeEndElement();
	}

	getItemType() {
		return 'headernode';
	}

	saveContent(writer, absolute) {
		super.saveContent(writer, absolute);

		writer.writeStartElement('sections');
		writer.writeStartArray('section');

		this.sectionData.sections.forEach((data, index) => {
			data.save(writer, index);
		});

		writer.writeEndArray('section');
		writer.writeEndElement();
	}

	_assignName(id) {
		this.setName(`Header + ${id}`);
	}

	read(reader, object) {
		let sections;

		if (reader.version >= 1) {
			sections = object;
			this.getHeaderAttributes().setOutlineDirection(reader.getAttributeString(object, 'outline', 'above'));
		} else {
			super.read(reader, object);
			sections = reader.getObject(object, 'sections');
			if (sections === undefined) {
				return;
			}
		}

		reader.iterateObjects(sections, (name, child) => {
			switch (name) {
				case 'section': {
					const index = reader.getAttribute(child, 'index');
					if (index !== undefined) {
						const section = new HeaderSection();
						section._size = this.getDefaultSectionSize();
						section.read(reader, child);
						if (!section.isDefault(this.getDefaultSectionSize())) {
							this.sectionData.sections[Number(index)] = section;
						}
					}
					break;
				}
				default:
					break;
			}
		});
	}

	updateOutlineOpen(index, direction) {
		const max = this.getSections();
		const level = this.getSectionLevel(index);
		let lev;
		let size = 0;

		if (direction === 'above') {
			lev = this.getSectionLevel(index + 1);
			if (lev > level) {
				let iSub = index + 1;
				size = 0;
				while (iSub < max && lev > level) {
					if (this.getSectionParent(iSub) === index) {
						size += this.getSectionSize(iSub);
					}
					lev = this.getSectionLevel(iSub);
					iSub += 1;
				}

				const closed = size ? undefined : true;
				let data = this.getSectionAt(index);
				if (closed || data) {
					data = this.getOrCreateSectionAt(index);
					data._closed = closed;
				}
			}
		} else {
			if (!index) {
				return;
			}
			lev = this.getSectionLevel(index - 1);
			if (lev > level) {
				let iSub = index - 1;
				size = 0;
				while (iSub > 0 && lev > level) {
					if (this.getSectionParent(iSub) === index) {
						size += this.getSectionSize(iSub);
					}
					lev = this.getSectionLevel(iSub);
					iSub -= 1;
				}

				const closed = size ? undefined : true;
				let data = this.getSectionAt(index);
				if (closed || data) {
					data = this.getOrCreateSectionAt(index);
					data._closed = closed;
				}
			}
		}
	}

	updateParents() {
		let level;
		let lastLevel = 0;
		let j;
		const parent = [];
		const direction = this.sectionData.outlineDirection;
		const max = this.sectionData.sections.length + 1;

		if (direction === 'above') {
			for (let i = 0; i < max; i += 1) {
				level = this.getSectionLevel(i);
				if (i === 0 && level > 0) {
					this.setSectionLevel(i, 0);
				}
				parent[level] = i;
				if (level) {
					j = level - 1;
					while (j > 0 && parent[j] === undefined) {
						j -= 1;
					}
					this.setSectionParent(i, i === parent[j] ? undefined : parent[j]);
				} else if (this.getSectionParent(i) !== undefined) {
					this.setSectionParent(i, undefined);
				}
				j = lastLevel;
				while (j > level) {
					parent[j] = undefined;
					j -= 1;
				}
				lastLevel = level;
			}
		} else {
			for (let i = max; i >= 0; i -= 1) {
				level = this.getSectionLevel(i);
				parent[level] = i;
				if (level) {
					j = level - 1;
					while (j > 0 && parent[j] === undefined) {
						j -= 1;
					}
					this.setSectionParent(i, i === parent[j] ? undefined : parent[j]);
				} else if (this.getSectionParent(i) !== undefined) {
					this.setSectionParent(i, undefined);
				}
				j = lastLevel;
				while (j > level) {
					parent[j] = undefined;
					j -= 1;
				}
				lastLevel = level;
			}
		}

		for (let i = 0; i < max; i += 1) {
			if (this.getSectionSize(i)) {
				this.updateOutlineOpen(i, direction);
			}
		}

		return false;
	}

	getInternalSize() {
		return new Point(this.getDefaultSectionSize() * this.getSections(), 1000);
	}

	getSectionFromReference(ref) {
		return this.sectionData.getSectionFromReference(ref);
	}

};
